import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  UserMinus,
  Building2,
  MoreHorizontal,
  Trash2,
  CheckCircle2,
  Circle,
  ArrowRight,
  BarChart3,
  ClipboardList,
  User,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface OffboardingTask {
  name: string
  completed: boolean
  completedAt: string | null
  completedBy: string | null
  notes?: string
}

interface OffboardingChecklist {
  id: string
  tenantId: string
  type: 'client' | 'employee'
  targetName: string
  targetEmail: string | null
  clientId: string | null
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  assigneeId: string | null
  tasks: OffboardingTask[]
  reason: string | null
  scheduledDate: string | null
  completedAt: string | null
  notes: string | null
  progress: number
  createdAt: string
  updatedAt: string
}

interface OffboardingStats {
  total: number
  active: number
  completed: number
  pending: number
  inProgress: number
  cancelled: number
  byType: Array<{ type: string; count: number }>
}

// ============================================
// Status Helpers
// ============================================

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  in_progress: { label: 'In Progress', variant: 'default' },
  completed: { label: 'Completed', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
}

// ============================================
// Create Dialog
// ============================================

function CreateOffboardingDialog({ defaultType }: { defaultType?: 'client' | 'employee' }) {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState<string>(defaultType || 'client')
  const [targetName, setTargetName] = useState('')
  const [targetEmail, setTargetEmail] = useState('')
  const [reason, setReason] = useState('')
  const [scheduledDate, setScheduledDate] = useState('')
  const [notes, setNotes] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/offboarding', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offboardings'] })
      queryClient.invalidateQueries({ queryKey: ['offboarding-stats'] })
      toast({ title: 'Offboarding checklist created' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create checklist', variant: 'destructive' }),
  })

  const resetForm = () => {
    setType(defaultType || 'client')
    setTargetName('')
    setTargetEmail('')
    setReason('')
    setScheduledDate('')
    setNotes('')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          {defaultType === 'employee' ? (
            <><UserMinus className="mr-2 h-4 w-4" /> Employee Offboard</>
          ) : defaultType === 'client' ? (
            <><Building2 className="mr-2 h-4 w-4" /> Client Offboard</>
          ) : (
            <><Plus className="mr-2 h-4 w-4" /> New Offboarding</>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Start Offboarding</DialogTitle>
          <DialogDescription>
            Create a new offboarding checklist. Default tasks will be pre-populated based on the type.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client Offboarding</SelectItem>
                <SelectItem value="employee">Employee Offboarding</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{type === 'client' ? 'Client Name' : 'Employee Name'}</Label>
            <Input value={targetName} onChange={(e) => setTargetName(e.target.value)} placeholder={type === 'client' ? 'Acme Corp' : 'John Smith'} />
          </div>
          <div className="space-y-2">
            <Label>Email (optional)</Label>
            <Input type="email" value={targetEmail} onChange={(e) => setTargetEmail(e.target.value)} placeholder="email@example.com" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contract_end">Contract End</SelectItem>
                  <SelectItem value="termination">Termination</SelectItem>
                  <SelectItem value="migration">Migration</SelectItem>
                  <SelectItem value="resignation">Resignation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Scheduled Date</Label>
              <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional context..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              type,
              targetName,
              targetEmail: targetEmail || undefined,
              reason: reason || undefined,
              scheduledDate: scheduledDate || undefined,
              notes: notes || undefined,
            })}
            disabled={!targetName || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Checklist'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Checklist Detail View
// ============================================

function ChecklistDetailDialog({ checklist }: { checklist: OffboardingChecklist }) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const toggleMutation = useMutation({
    mutationFn: ({ taskIndex, completed }: { taskIndex: number; completed: boolean }) =>
      apiClient.patch(`/offboarding/${checklist.id}/tasks/${taskIndex}`, { completed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offboardings'] })
      queryClient.invalidateQueries({ queryKey: ['offboarding-stats'] })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' }),
  })

  const tasks = checklist.tasks || []
  const completedCount = tasks.filter((t) => t.completed).length

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1">
          <ClipboardList className="h-4 w-4" /> View
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {checklist.type === 'client' ? (
              <Building2 className="h-5 w-5" />
            ) : (
              <UserMinus className="h-5 w-5" />
            )}
            {checklist.targetName}
          </DialogTitle>
          <DialogDescription>
            {checklist.type === 'client' ? 'Client' : 'Employee'} offboarding &mdash;{' '}
            {statusConfig[checklist.status]?.label || checklist.status}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{completedCount}/{tasks.length} tasks ({checklist.progress}%)</span>
            </div>
            <div className="h-3 w-full rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${checklist.progress}%` }}
              />
            </div>
          </div>

          {/* Meta info */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {checklist.targetEmail && (
              <div>
                <span className="text-muted-foreground">Email: </span>
                {checklist.targetEmail}
              </div>
            )}
            {checklist.reason && (
              <div>
                <span className="text-muted-foreground">Reason: </span>
                {checklist.reason.replace('_', ' ')}
              </div>
            )}
            {checklist.scheduledDate && (
              <div>
                <span className="text-muted-foreground">Scheduled: </span>
                {new Date(checklist.scheduledDate).toLocaleDateString()}
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Created: </span>
              {new Date(checklist.createdAt).toLocaleDateString()}
            </div>
          </div>

          {checklist.notes && (
            <div className="text-sm p-3 rounded-md bg-muted">
              <span className="text-muted-foreground">Notes: </span>{checklist.notes}
            </div>
          )}

          {/* Task List */}
          <div className="space-y-1">
            <h4 className="font-medium text-sm">Tasks</h4>
            <div className="space-y-1">
              {tasks.map((task, index) => (
                <button
                  key={index}
                  className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-muted transition-colors text-left"
                  onClick={() => toggleMutation.mutate({ taskIndex: index, completed: !task.completed })}
                  disabled={checklist.status === 'cancelled' || toggleMutation.isPending}
                >
                  {task.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
                  )}
                  <span className={task.completed ? 'line-through text-muted-foreground' : ''}>
                    {task.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Kanban Column
// ============================================

function KanbanColumn({
  title,
  status,
  checklists,
  onDelete,
}: {
  title: string
  status: string
  checklists: OffboardingChecklist[]
  onDelete: (id: string) => void
}) {
  const columnColors: Record<string, string> = {
    pending: 'border-t-yellow-500',
    in_progress: 'border-t-blue-500',
    completed: 'border-t-green-500',
    cancelled: 'border-t-red-500',
  }

  return (
    <div className="flex-1 min-w-[280px]">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold text-sm">{title}</h3>
        <Badge variant="secondary" className="text-xs">{checklists.length}</Badge>
      </div>
      <div className="space-y-3">
        {checklists.map((cl) => (
          <Card key={cl.id} className={`border-t-2 ${columnColors[status] || ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {cl.type === 'client' ? (
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <User className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="font-medium text-sm">{cl.targetName}</span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={() => onDelete(cl.id)}>
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                <Badge variant="outline" className="text-xs">
                  {cl.type === 'client' ? 'Client' : 'Employee'}
                </Badge>
                {cl.reason && (
                  <Badge variant="secondary" className="text-xs">
                    {cl.reason.replace('_', ' ')}
                  </Badge>
                )}
              </div>

              {/* Progress bar */}
              <div className="space-y-1 mb-3">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{cl.tasks.filter((t) => t.completed).length}/{cl.tasks.length} tasks</span>
                  <span>{cl.progress}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${cl.progress}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {cl.scheduledDate
                    ? new Date(cl.scheduledDate).toLocaleDateString()
                    : new Date(cl.createdAt).toLocaleDateString()}
                </span>
                <ChecklistDetailDialog checklist={cl} />
              </div>
            </CardContent>
          </Card>
        ))}
        {checklists.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-8 border border-dashed rounded-md">
            No items
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function OffboardingPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState('kanban')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: listData, isLoading } = useQuery({
    queryKey: ['offboardings', typeFilter],
    queryFn: () => apiClient.get<{ data: OffboardingChecklist[]; meta: { pagination: unknown } }>(
      '/offboarding',
      {
        limit: 100,
        ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
      }
    ),
  })

  const { data: statsData } = useQuery({
    queryKey: ['offboarding-stats'],
    queryFn: () => apiClient.get<OffboardingStats>('/offboarding/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/offboarding/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offboardings'] })
      queryClient.invalidateQueries({ queryKey: ['offboarding-stats'] })
      toast({ title: 'Checklist deleted' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete checklist', variant: 'destructive' }),
  })

  const checklists: OffboardingChecklist[] = listData?.data ?? (listData as any)?.data?.data ?? []
  const stats = statsData?.data ?? null

  const filtered = useMemo(() => {
    if (!search) return checklists
    const lower = search.toLowerCase()
    return checklists.filter(
      (c) =>
        c.targetName.toLowerCase().includes(lower) ||
        c.targetEmail?.toLowerCase().includes(lower)
    )
  }, [checklists, search])

  const byStatus = useMemo(() => ({
    pending: filtered.filter((c) => c.status === 'pending'),
    in_progress: filtered.filter((c) => c.status === 'in_progress'),
    completed: filtered.filter((c) => c.status === 'completed'),
    cancelled: filtered.filter((c) => c.status === 'cancelled'),
  }), [filtered])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Offboarding</h1>
          <p className="text-muted-foreground">Client and employee offboarding checklists</p>
        </div>
        <div className="flex items-center gap-2">
          <CreateOffboardingDialog defaultType="client" />
          <CreateOffboardingDialog defaultType="employee" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{stats?.active ?? 0}</div>
                <p className="text-xs text-muted-foreground">Active Offboardings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Circle className="h-4 w-4 text-yellow-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.pending ?? 0}</div>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-blue-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.inProgress ?? 0}</div>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.completed ?? 0}</div>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="client">Client</SelectItem>
            <SelectItem value="employee">Employee</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs: Kanban vs List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
          <TabsTrigger value="list">List</TabsTrigger>
        </TabsList>

        <TabsContent value="kanban" className="mt-4">
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4">
              <KanbanColumn title="Pending" status="pending" checklists={byStatus.pending} onDelete={(id) => deleteMutation.mutate(id)} />
              <KanbanColumn title="In Progress" status="in_progress" checklists={byStatus.in_progress} onDelete={(id) => deleteMutation.mutate(id)} />
              <KanbanColumn title="Completed" status="completed" checklists={byStatus.completed} onDelete={(id) => deleteMutation.mutate(id)} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-5 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12">
                <ClipboardList className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No offboarding checklists found</p>
                <p className="text-sm text-muted-foreground mt-1">Create one using the buttons above</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filtered.map((cl) => {
                const completedCount = cl.tasks.filter((t) => t.completed).length
                return (
                  <Card key={cl.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {cl.type === 'client' ? (
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <UserMinus className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div>
                            <div className="font-medium">{cl.targetName}</div>
                            <div className="text-sm text-muted-foreground">
                              {cl.type === 'client' ? 'Client' : 'Employee'} offboarding
                              {cl.reason && ` - ${cl.reason.replace('_', ' ')}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right mr-2">
                            <div className="text-sm font-medium">{completedCount}/{cl.tasks.length} tasks</div>
                            <div className="h-1.5 w-24 rounded-full bg-secondary overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${cl.progress}%` }}
                              />
                            </div>
                          </div>
                          <Badge variant={statusConfig[cl.status]?.variant || 'secondary'}>
                            {statusConfig[cl.status]?.label || cl.status}
                          </Badge>
                          <ChecklistDetailDialog checklist={cl} />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(cl.id)}>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
